/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import {
  acceptTermsAndConditions,
  createCluster,
  deleteCluster,
  getCluster,
  getTermsAndConditions,
  listClusters,
  patchCluster,
} from '@modules/ccm-api/v1/callers'
import type {
  Cluster,
  ClusterCreated,
  ClusterId,
  ClusterList,
  CreateClusterRequest,
  MultiErrorResponse,
  operations,
  PatchClusterRequest,
  TermsAndConditionsInfo,
} from '@modules/ccm-api/v1/types'
import { useOrganizationId } from '@modules/permissions-lib/hooks'
import { queryClient } from '@modules/query'

import { isCCMFeatureActivated } from './feature'

import type { UseMutationOptions, UseQueryOptions, UseQueryResult } from 'react-query'

const CCM_LIST_CLUSTER_QUERY_KEY = 'ccmClusterList'

const CCM_CLUSTER_QUERY_KEY = 'ccmCluster'

export const useGetCCMClusters = (
  options?: Omit<UseQueryOptions<ClusterList, MultiErrorResponse>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<ClusterList, MultiErrorResponse>(CCM_LIST_CLUSTER_QUERY_KEY, () => listClusters(), {
    ...options,
    enabled: isCCMFeatureActivated() && (options?.enabled ?? true),
  })

export const usePatchCCMCluster = (
  options?: Omit<
    UseMutationOptions<
      Cluster,
      MultiErrorResponse,
      {
        id: ClusterId
        body: PatchClusterRequest
      }
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    Cluster,
    MultiErrorResponse,
    {
      id: ClusterId
      body: PatchClusterRequest
    }
  >(
    ({ id, body }) =>
      patchCluster({
        pathParameters: {
          id,
        },
        body,
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)

        const [, { id }] = args

        queryClient.invalidateQueries([CCM_CLUSTER_QUERY_KEY, id])

        options?.onSuccess?.(...args)
      },
    },
  )

export const useDeleteCCMCluster = (
  options?: Omit<
    UseMutationOptions<
      void,
      MultiErrorResponse,
      {
        id: ClusterId
      }
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    void,
    MultiErrorResponse,
    {
      id: ClusterId
    }
  >(
    ({ id }) =>
      deleteCluster({
        pathParameters: {
          id,
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)

        const [, { id }] = args

        queryClient.invalidateQueries([CCM_CLUSTER_QUERY_KEY, id])

        options?.onSuccess?.(...args)
      },
    },
  )

export const useGetTermsAndConditionsQuery = (): UseQueryResult<
  TermsAndConditionsInfo,
  MultiErrorResponse
> & { hasNoOrg: boolean } => {
  const isOrganizationIdHookEnabled = isCCMFeatureActivated()

  const { isOrganizationIdFetched, organizationId } = useOrganizationId({
    enabled: isOrganizationIdHookEnabled,
  })

  const useQueryResponse = useQuery<TermsAndConditionsInfo, MultiErrorResponse>(
    ['ccmAcceptedTos'],
    () =>
      getTermsAndConditions({
        pathParameters: {
          organization_id: organizationId ?? '',
        },
      }),
    {
      enabled: isOrganizationIdHookEnabled && organizationId !== undefined,
    },
  )
  return {
    ...useQueryResponse,
    hasNoOrg: isOrganizationIdFetched && organizationId === undefined,
  }
}

export const useIsCCMTosAccepted = () => {
  const { data } = useGetTermsAndConditionsQuery()

  return data?.accepted !== undefined
}

export const useUpdateCCMAcceptedTos = (
  options?: Omit<
    UseMutationOptions<TermsAndConditionsInfo, MultiErrorResponse, string>,
    'mutationFn'
  >,
) =>
  useMutation<TermsAndConditionsInfo, MultiErrorResponse, string>(
    (organizationId: string) =>
      acceptTermsAndConditions({
        pathParameters: {
          organization_id: organizationId,
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries('ccmAcceptedTos')

        options?.onSuccess?.(...args)
      },
    },
  )

export const useGetCCMCluster = (
  clusterId: string,
  options?: Omit<UseQueryOptions<Cluster, MultiErrorResponse>, 'queryKey' | 'queryFn'>,
) =>
  useQuery<Cluster, MultiErrorResponse>(
    [CCM_CLUSTER_QUERY_KEY, clusterId],
    () =>
      getCluster({
        pathParameters: {
          id: clusterId,
        },
      }),
    {
      ...options,
      enabled: isCCMFeatureActivated() && (options?.enabled ?? true),
    },
  )

export const useCreateCCMCluster = (
  options?: Omit<
    UseMutationOptions<ClusterCreated, MultiErrorResponse, CreateClusterRequest>,
    'mutationFn'
  >,
  queryParameters?: operations['createCluster']['parameters']['query'],
) =>
  useMutation<ClusterCreated, MultiErrorResponse, CreateClusterRequest>(
    (body) =>
      createCluster({
        body,
        queryParameters,
      }),
    {
      ...options,
    },
  )

export const useDisableCCMClusterService = (
  options?: Omit<
    UseMutationOptions<
      Cluster,
      MultiErrorResponse,
      {
        id: ClusterId
        serviceName: keyof Cluster['services']
      }
    >,
    'mutationFn'
  >,
) =>
  useMutation<
    Cluster,
    MultiErrorResponse,
    {
      id: ClusterId
      serviceName: keyof Cluster['services']
    }
  >(
    ({ id, serviceName }) =>
      patchCluster({
        pathParameters: {
          id,
        },
        body: {
          services: {
            [serviceName]: {
              enabled: false,
            },
          },
        },
      }),
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries(CCM_LIST_CLUSTER_QUERY_KEY)

        const [, { id }] = args

        queryClient.invalidateQueries([CCM_CLUSTER_QUERY_KEY, id])

        options?.onSuccess?.(...args)
      },
    },
  )
